import React from "react"

import { Button, Popover, PopoverContent, PopoverTrigger } from "@coval/ui"
import { Palette } from "lucide-react"

import { ColorGridPicker } from "@/components/forms/ColorGridPicker"

import type { DistinctCompareByValue } from "../hooks/useCompareByFeature"
import type { CompareByDimension } from "../types"

const COMPARE_BY_COLOR_TITLE_BY_DIMENSION: Record<CompareByDimension, string> =
  {
    run: "Run colors",
    agent: "Agent colors",
    mutation: "Mutation colors",
    persona: "Persona colors",
    test_case: "Test case colors",
    metadata: "Metadata colors",
    tag: "Tag colors",
    custom: "Custom group colors",
  }

interface CompareByColorSectionProps {
  distinctCompareByValues: DistinctCompareByValue[]
  compareByValueToColor: Record<string, string>
  setCompareByColor: (overrideKey: string, color: string) => void
}

/** Color pickers for the active compare-by values, without popover chrome. */
export const CompareByColorSection: React.FC<CompareByColorSectionProps> = ({
  distinctCompareByValues,
  compareByValueToColor,
  setCompareByColor,
}) => {
  return (
    <div className="flex flex-wrap gap-3">
      {distinctCompareByValues.map(({ overrideKey, displayName }) => {
        const color = compareByValueToColor[overrideKey]
        return (
          <div
            key={overrideKey}
            className="flex items-center gap-2"
            role="group"
            aria-label={`Color for ${displayName}`}
          >
            <ColorGridPicker
              value={color}
              onChange={(c) => setCompareByColor(overrideKey, c)}
              label={`Set color for ${displayName}`}
            />
            <span
              className="text-sm text-coval-text-muted truncate max-w-[120px]"
              style={{ color }}
              title={displayName}
            >
              {displayName.length <= 6
                ? displayName
                : `${displayName.slice(0, 6)}…`}
            </span>
          </div>
        )
      })}
    </div>
  )
}

interface CompareByConfigProps extends CompareByColorSectionProps {
  compareBy: CompareByDimension
}

export const CompareByConfig: React.FC<CompareByConfigProps> = ({
  compareBy,
  ...sectionProps
}) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="secondary" icon tooltip="Compare by color settings">
          <Palette className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="p-3 max-w-sm">
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium text-coval-text-muted">
            {COMPARE_BY_COLOR_TITLE_BY_DIMENSION[compareBy]}
          </span>
          <CompareByColorSection {...sectionProps} />
        </div>
      </PopoverContent>
    </Popover>
  )
}
